import {
  Anchor,
  Badge,
  Button,
  Code,
  Group,
  Modal,
  Pagination,
  SegmentedControl,
  Select,
  Spoiler,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { stanjeZahteve } from '../asistent/asistent';
import { stevilo } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { Oznaka } from '../strani/PregledRacunov';
import { barve } from '../tema';
import { KLJUC_ASISTENT_SKRBNIK as KLJUC, klic } from './ai';
import { cas } from './skrbnistvo';
import { glave } from './zeton';

type Zahteva = Shema['AsistentManjkajoce'];

const VELIKOST = 25;

const FILTRI = [
  { value: 'odprte', label: 'Odprte' },
  { value: 'nove', label: 'Nove' },
  { value: 'v_delu', label: 'V delu' },
  { value: 'urejene', label: 'Urejene' },
  { value: 'zavrnjene', label: 'Zavrnjene' },
  { value: 'vse', label: 'Vse' },
];

const STANJA = [
  { value: 'NOVO', label: 'Novo' },
  { value: 'V_DELU', label: 'V delu' },
  { value: 'UREJENO', label: 'Urejeno' },
  { value: 'ZAVRNJENO', label: 'Zavrnjeno' },
];

/** Predlog odgovora uporabniku ob izbiri stanja (skrbnik ga lahko popravi). */
const ODGOVORI: Record<string, string> = {
  UREJENO: 'Asistent zdaj zna tudi to – vprašajte ga znova. Hvala za predlog!',
  ZAVRNJENO: 'Hvala za predlog. Tega asistentu za zdaj ne bomo dodali, ker …',
};

/**
 * Skrbništvo → Asistent: zahteve, ki jih asistent še ne zna (iz vseh instanc), da jih hitro dodamo – stanje, interna
 * opomba in odgovor uporabniku v povratno informacijo; spodaj vsa orodja, kot jih vidi model.
 */
export function AsistentSkrbnistvo() {
  return (
    <Stack gap="md">
      <Zahteve />
      <Zmoznosti />
    </Stack>
  );
}

function Zahteve() {
  const [filter, setFilter] = useState('odprte');
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje.trim(), 300);
  const [stran, setStran] = useState(0);
  const [izbrana, setIzbrana] = useState<Zahteva | null>(null);
  const seznam = useQuery({
    queryKey: [...KLJUC, 'manjkajoce', filter, zakasnjeno, stran],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      klic(() =>
        podatki(
          api.GET('/api/skrbnik/asistent/manjkajoce', {
            signal,
            headers: glave(),
            params: { query: { stanje: filter, iskanje: zakasnjeno || undefined, stran, velikost: VELIKOST } },
          }),
        ),
      ),
  });
  const skupaj = seznam.data?.skupaj ?? 0;
  const strani = Math.max(1, Math.ceil(skupaj / VELIKOST));

  return (
    <Kartica naslov="Česar asistent še ne zna">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Vprašanja uporabnikov, za katera asistent nima orodja, s povzetkom in predlogom modela. Vsak pogovor je odprl
          povratno informacijo; ko orodje dodamo, zahtevo označite kot urejeno in uporabniku odgovorite.
        </Text>
        <Group gap="sm" wrap="wrap">
          <SegmentedControl
            aria-label="Stanje"
            data={FILTRI}
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setStran(0);
            }}
          />
          <TextInput
            aria-label="Iskanje po zahtevah"
            placeholder="Instanca, avtor, vprašanje, predlog"
            leftSection={<IconSearch size={16} />}
            value={iskanje}
            onChange={(e) => {
              setIskanje(e.currentTarget.value);
              setStran(0);
            }}
            w={{ base: '100%', sm: 320 }}
          />
        </Group>
        {!seznam.data ? (
          <StanjeNalaganja
            napaka={seznam.error}
            ponovi={() => void seznam.refetch()}
            naslov="Zahtev ni bilo mogoče naložiti"
          />
        ) : seznam.data.vsebina?.length ? (
          <>
            <Table.ScrollContainer minWidth={1000}>
              <Table highlightOnHover verticalSpacing={8}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Čas</Table.Th>
                    <Table.Th>Instanca</Table.Th>
                    <Table.Th>Vprašanje</Table.Th>
                    <Table.Th>Področje</Table.Th>
                    <Table.Th>Predlog modela</Table.Th>
                    <Table.Th>Stanje</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {seznam.data.vsebina.map((z) => {
                    const s = stanjeZahteve(z.stanje);
                    return (
                      <Table.Tr
                        key={z.id}
                        style={{ cursor: 'pointer', fontWeight: z.stanje === 'NOVO' ? 700 : undefined }}
                        onClick={() => setIzbrana(z)}
                      >
                        <Table.Td className="brez-preloma">{cas(z.cas)}</Table.Td>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/skrbnistvo/${z.companyId}`}
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {z.instanca}
                          </Anchor>
                          <Text size="xs" c="dimmed">
                            {z.avtor}
                          </Text>
                        </Table.Td>
                        <Table.Td maw={360}>
                          <Text size="sm" lineClamp={3}>
                            {z.vprasanje}
                          </Text>
                          {z.povzetek && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {z.povzetek}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td className="brez-preloma">{z.podrocje}</Table.Td>
                        <Table.Td maw={280}>
                          <Text size="sm" lineClamp={3}>
                            {z.predlog}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {stevilo(stran * VELIKOST + 1)}–{stevilo(Math.min(skupaj, (stran + 1) * VELIKOST))} od {stevilo(skupaj)}
              </Text>
              {strani > 1 && (
                <Pagination total={strani} value={stran + 1} onChange={(v) => setStran(v - 1)} size="sm" />
              )}
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            {zakasnjeno ? 'Ni zahtev, ki bi ustrezale iskanju.' : 'Ni zahtev v tem stanju.'}
          </Text>
        )}
      </Stack>
      {izbrana && <UrediZahtevo key={izbrana.id} z={izbrana} zapri={() => setIzbrana(null)} />}
    </Kartica>
  );
}

/** Stanje, interna opomba in odgovor uporabniku (v povratno informacijo, dobi e-pošto). */
function UrediZahtevo({ z, zapri }: { z: Zahteva; zapri: () => void }) {
  const odjemalec = useQueryClient();
  const [stanje, setStanje] = useState(z.stanje ?? 'NOVO');
  const [opomba, setOpomba] = useState(z.opomba ?? '');
  const [odgovor, setOdgovor] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      await klic(() =>
        podatki(
          api.PUT('/api/skrbnik/asistent/manjkajoce/{id}', {
            headers: glave(),
            params: { path: { id: z.id ?? 0 } },
            body: {
              stanje: stanje === z.stanje ? undefined : stanje,
              opomba: opomba.trim() === (z.opomba ?? '') ? undefined : opomba.trim(),
              odgovor: odgovor.trim() || undefined,
            },
          }),
        ),
      );
      await odjemalec.invalidateQueries({ queryKey: KLJUC });
      zapri();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Modal opened onClose={zapri} title="Zahteva za asistenta" radius="lg" size="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {cas(z.cas)} · {z.instanca} · {z.avtor}
        </Text>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          »{z.vprasanje}«
        </Text>
        {z.povzetek && (
          <Text size="sm">
            <b>Povzetek:</b> {z.povzetek}
          </Text>
        )}
        {z.predlog && (
          <Text size="sm">
            <b>Predlog modela:</b> {z.predlog}
          </Text>
        )}
        {z.povratnaInformacijaId && (
          <Anchor component={Link} to={`/skrbnistvo/povratne-informacije/${z.povratnaInformacijaId}`} size="sm">
            Odpri povratno informacijo #{z.povratnaInformacijaId}
          </Anchor>
        )}
        <Select
          label="Stanje"
          data={STANJA}
          value={stanje}
          allowDeselect={false}
          error={napake.stanje}
          onChange={(v) => {
            const novo = v ?? stanje;
            setStanje(novo);
            if (!odgovor.trim() && ODGOVORI[novo] && z.povratnaInformacijaId) {
              setOdgovor(ODGOVORI[novo]);
            }
          }}
        />
        <Textarea
          label="Interna opomba"
          description="Vidi jo samo skrbništvo, npr. v kateri različici je dodano."
          autosize
          minRows={2}
          value={opomba}
          error={napake.opomba}
          onChange={(e) => setOpomba(e.currentTarget.value)}
        />
        {z.povratnaInformacijaId && (
          <Textarea
            label="Odgovor uporabniku"
            description="Gre v njegovo povratno informacijo; uporabniki, ki so v niti pisali, dobijo e-pošto. Prazno = brez odgovora."
            autosize
            minRows={3}
            value={odgovor}
            error={napake.odgovor}
            onChange={(e) => setOdgovor(e.currentTarget.value)}
          />
        )}
        {z.spremenil && (
          <Text size="xs" c="dimmed">
            Nazadnje spremenil {z.spremenil}, {cas(z.spremenjeno)}
          </Text>
        )}
        {napaka && (
          <Text size="sm" c="red" role="alert">
            {napaka}
          </Text>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri}>
            Prekliči
          </Button>
          <Button loading={dela} onClick={() => void shrani()}>
            {odgovor.trim() ? 'Shrani in odgovori' : 'Shrani'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Vsa orodja asistenta iz kode strežnika, z opisom in parametri, kot ju dobi model. */
function Zmoznosti() {
  const zmoznosti = useQuery({
    queryKey: [...KLJUC, 'zmoznosti'],
    queryFn: ({ signal }) =>
      klic(() => podatki(api.GET('/api/skrbnik/asistent/zmoznosti', { signal, headers: glave() }))),
  });
  return (
    <Kartica naslov="Zmožnosti asistenta">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Orodja so določena v kodi strežnika (v vseh okoljih enaka); novo orodje pride z novo različico. Ton odgovorov
          uredite v zavihku Umetna inteligenca → Navodila funkcij → Asistent.
        </Text>
        {!zmoznosti.data ? (
          <StanjeNalaganja
            napaka={zmoznosti.error}
            ponovi={() => void zmoznosti.refetch()}
            naslov="Zmožnosti ni bilo mogoče naložiti"
          />
        ) : (
          zmoznosti.data.map((z) => (
            <Stack key={z.orodje} gap={4} pb="sm" style={{ borderBottom: `1px solid ${barve.obroba}` }}>
              <Group gap="xs">
                <Text fw={700}>{z.naziv}</Text>
                <Code>{z.orodje}</Code>
                <Badge size="sm" variant="light" color="gray">
                  {z.podrocje}
                </Badge>
                {!z.vidno && (
                  <Badge size="sm" variant="light" color="orange">
                    ni v seznamu za uporabnike
                  </Badge>
                )}
              </Group>
              <Text size="sm">{z.opis}</Text>
              <Text size="sm" c="dimmed">
                Za model: {z.opisZaModel}
              </Text>
              <Spoiler maxHeight={0} showLabel="Parametri" hideLabel="Skrij parametre">
                <Code block>{z.parametri}</Code>
              </Spoiler>
            </Stack>
          ))
        )}
      </Stack>
    </Kartica>
  );
}
